import { createContext } from "react";
import openSocket from "socket.io-client";
import { getBackendSocketURL } from "../../services/config";
import { decodeToken, isExpired } from "react-jwt";
import api from "../../services/api";
/** Helper: Centralizar manejo de tokens */
function getValidToken() {
  const token = JSON.parse(localStorage.getItem("token"));
  if (!token || isExpired(token)) {
    console.debug("Token is invalid or expired");
    return null;
  }
  return token;
}
class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = new Map(); // Usar Map para eventos y callbacks
    this.joins = new Set(); // Usar Set para evitar duplicados
    this.rawSocket.on("connect", () => {
      if (this.rawSocket.io.opts.query?.r && !this.rawSocket.recovered) {
        const refreshJoinsOnReady = () => {
          for (const join of this.joins) {
            const { event, params } = JSON.parse(join);
            console.debug("Refreshing join", { event, params });
            this.rawSocket.emit(`join${event}`, ...params);
          }
          this.rawSocket.off("ready", refreshJoinsOnReady);
        };
        for (const [event, callbacks] of this.callbacks) {
          callbacks.forEach((callback) => {
            this.rawSocket.off(event, callback);
            this.rawSocket.on(event, callback);
          });
        }
        this.rawSocket.on("ready", refreshJoinsOnReady);
      }
    });
  }
  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback);
    }
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, []);
    }
    this.callbacks.get(event).push(callback);
    return this.rawSocket.on(event, callback);
  }
  off(event, callback) {
    if (this.callbacks.has(event)) {
      const eventCallbacks = this.callbacks.get(event).filter((cb) => cb !== callback);
      if (eventCallbacks.length > 0) {
        this.callbacks.set(event, eventCallbacks);
      } else {
        this.callbacks.delete(event);
      }
      this.rawSocket.off(event, callback);
    }
  }
  emit(event, ...params) {
    if (event.startsWith("join")) {
      const joinEvent = JSON.stringify({ event: event.substring(4), params });
      this.joins.add(joinEvent); // Guardar como string para evitar duplicados
      console.debug("Joining", { event: event.substring(4), params });
    }
    return this.rawSocket.emit(event, ...params);
  }
  disconnect() {
    for (const join of this.joins) {
      const { event, params } = JSON.parse(join);
      this.rawSocket.emit(`leave${event}`, ...params);
    }
    this.joins.clear();
    for (const [event, callbacks] of this.callbacks) {
      callbacks.forEach((callback) => {
        this.rawSocket.off(event, callback);
      });
    }
    this.callbacks.clear();
  }
}
class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}
const socketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,
  visibilityListenerAdded: false,
  healthCheckInterval: null,
  GetSocket: function (_discardCompanyId = null) {
    const token = getValidToken();
    if (!token) {
      return new DummySocket();
    }
    const { userId, companyId } = decodeToken(token);
    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        console.debug("Closing old socket - company or user changed");
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
        this.stopHealthCheck();
      }
      if (isExpired(token)) {
        console.debug("Expired token, refreshing token");
        api.get("/auth/me").then((response) => {
          console.debug("Token refreshed", response);
          window.location.reload();
        });
        return new DummySocket();
      }
      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      this.currentSocket = openSocket(getBackendSocketURL(), {
        transports: ["websocket"],
        pingTimeout: 18000,
        pingInterval: 18000,
        // Use auth function instead of static query - gets fresh token on each reconnect
        auth: (cb) => {
          const freshToken = getValidToken();
          console.log(`[${new Date().toISOString()}] [SOCKET] Auth callback - providing fresh token`);
          cb({ token: freshToken });
        },
        query: { r: 0 }, // Keep query for recovery flag
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });
      // Log reconnect attempts (token is automatically refreshed via auth callback)
      this.currentSocket.io.on("reconnect_attempt", (attemptNumber) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] 🔄 Reconnect attempt #${attemptNumber} (auth callback will provide fresh token)`);
        // Set recovery flag
        this.currentSocket.io.opts.query.r = 1;
      });
      this.currentSocket.io.on("reconnect", (attemptNumber) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] ✅ Reconnected after ${attemptNumber} attempts`);
        this.socketReady = true;
      });
      this.currentSocket.io.on("reconnect_failed", () => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] ❌ Reconnect FAILED after all attempts`);
        // Force manual reconnect with fresh token
        this.forceReconnect();
      });
      this.currentSocket.on("disconnect", (reason) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] ❌ DISCONNECTED. Reason: ${reason}`);
        this.socketReady = false;
        // Only manually reconnect if socket.io won't auto-reconnect
        // (io client disconnect or io server disconnect = manual disconnect, won't auto-reconnect)
        if (reason === "io client disconnect" || reason === "io server disconnect") {
          console.log(`[${timestamp}] [SOCKET] Manual disconnect detected, forcing reconnect...`);
          setTimeout(() => this.forceReconnect(), 2000);
        }
        // For other reasons (transport close, ping timeout), socket.io will auto-reconnect
      });
      this.currentSocket.on("connect_error", (err) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] ❌ Connection ERROR: ${err.message}`);
        // Check if token expired
        const token = getValidToken();
        if (!token) {
          console.log(`[${timestamp}] [SOCKET] Token expired, refreshing page...`);
          window.location.reload();
        }
        // Otherwise let socket.io retry automatically
      });
      this.currentSocket.on("connect", (...params) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] [SOCKET] ✅ CONNECTED successfully`);
        this.socketReady = true;
      });
      this.currentSocket.onAny((event, ...args) => {
        console.debug("Event: ", { socket: this.currentSocket, event, args });
      });
      this.onReady(() => {
        this.socketReady = true;
      });
      // Add visibility change listener (only once)
      this.setupVisibilityListener();
      // Start health check
      this.startHealthCheck();
    }
    return new ManagedSocket(this);
  },
  setupVisibilityListener: function () {
    if (this.visibilityListenerAdded) return;
    this.visibilityListenerAdded = true;
    
    document.addEventListener("visibilitychange", () => {
      const timestamp = new Date().toISOString();
      if (document.visibilityState === "visible") {
        const isConnected = this.currentSocket?.connected;
        const isReconnecting = this.currentSocket?.io?._reconnecting;
        console.log(`[${timestamp}] [SOCKET] Tab became VISIBLE. Connected: ${isConnected}, Reconnecting: ${isReconnecting}`);
        // When tab becomes visible, check if socket is connected
        if (this.currentSocket && !this.currentSocket.connected && !isReconnecting) {
          console.log(`[${timestamp}] [SOCKET] ⚠️ Socket was DISCONNECTED while tab was hidden. Force reconnecting...`);
          this.forceReconnect();
        }
      } else {
        console.log(`[${timestamp}] [SOCKET] Tab became HIDDEN`);
      }
    });
    
    // Also handle window focus
    window.addEventListener("focus", () => {
      const timestamp = new Date().toISOString();
      const isConnected = this.currentSocket?.connected;
      const isReconnecting = this.currentSocket?.io?._reconnecting;
      console.log(`[${timestamp}] [SOCKET] Window FOCUSED. Connected: ${isConnected}, Reconnecting: ${isReconnecting}`);
      if (this.currentSocket && !this.currentSocket.connected && !isReconnecting) {
        console.log(`[${timestamp}] [SOCKET] ⚠️ Socket DISCONNECTED on focus. Force reconnecting...`);
        this.forceReconnect();
      }
    });
    
    console.log(`[${new Date().toISOString()}] [SOCKET] Visibility and focus listeners INITIALIZED`);
  },
  startHealthCheck: function () {
    // Check socket health every 30 seconds
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    this.healthCheckInterval = setInterval(() => {
      const timestamp = new Date().toISOString();
      const isConnected = this.currentSocket?.connected;
      const isReconnecting = this.currentSocket?.io?._reconnecting;
      if (this.currentSocket && !isConnected && !isReconnecting) {
        console.log(`[${timestamp}] [SOCKET] ⚠️ Health check FAILED - socket disconnected and not reconnecting. Force reconnecting...`);
        this.forceReconnect();
      } else if (!isConnected && isReconnecting) {
        console.log(`[${timestamp}] [SOCKET] ℹ️ Health check - socket reconnecting...`);
      } else {
        // Log healthy status every 5 minutes (every 10th check)
        if (Math.random() < 0.1) {
          console.log(`[${timestamp}] [SOCKET] ✅ Health check OK - socket connected`);
        }
      }
    }, 30000);
    console.log(`[${new Date().toISOString()}] [SOCKET] Health check STARTED (every 30s)`);
  },
  stopHealthCheck: function () {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  },
  forceReconnect: function () {
    const timestamp = new Date().toISOString();
    if (this.currentSocket?.connected) {
      console.log(`[${timestamp}] [SOCKET] Already connected, skipping force reconnect`);
      return;
    }
    // Check if token is valid (auth callback will get fresh one)
    const tokenValid = getValidToken();
    if (!tokenValid) {
      console.log(`[${timestamp}] [SOCKET] ⚠️ Token expired - refreshing page`);
      window.location.reload();
      return;
    }
    console.log(`[${timestamp}] [SOCKET] 🔄 FORCE RECONNECTING (auth callback will provide fresh token)...`);
    this.currentSocket.io.opts.query.r = 1;
    // Disconnect first to reset state, then connect
    this.currentSocket.disconnect();
    setTimeout(() => {
      this.currentSocket.connect();
    }, 500);
  },
  handleReconnect: function () {
    // Triggers socket.io's built-in reconnect or manual connect
    const timestamp = new Date().toISOString();
    if (this.currentSocket?.connected) {
      console.log(`[${timestamp}] [SOCKET] Already connected, skipping reconnect`);
      return;
    }
    // Check if token is valid
    const tokenValid = getValidToken();
    if (!tokenValid) {
      console.log(`[${timestamp}] [SOCKET] ⚠️ Token expired - refreshing page`);
      window.location.reload();
      return;
    }
    console.log(`[${timestamp}] [SOCKET] 🔄 Triggering reconnect (auth callback will provide fresh token)...`);
    this.currentSocket.io.opts.query.r = 1;
    // Don't call connect() if socket.io is already trying to reconnect
    if (!this.currentSocket.io._reconnecting) {
      this.currentSocket.connect();
    }
  },
  onReady: function (callbackReady) {
    if (this.socketReady) {
      callbackReady();
      return;
    }
    if (!this.currentSocket) {
      return;
    }
    const onReadyHandler = () => {
      this.socketReady = true;
      callbackReady();
      this.currentSocket.off("ready", onReadyHandler); // Asegurar limpieza
    };
    this.currentSocket.on("ready", onReadyHandler);
  },
  onConnect: function( callbackReady ) { this.onReady( callbackReady ) },
};
const SocketContext = createContext();
const SocketProvider = ({ children }) => {
  const socket = socketManager.GetSocket();
  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};
export { SocketContext, SocketProvider, socketManager };